import { useContext, useEffect, useState } from "react";
import { t } from "i18next";

import { LoansContext } from "../../../../../../context/LoansContext";

import BuyDownOptions from "./BuyDownOptions";

import { formatCurrency } from "../../../../../../utils/v2/common";
import { SizingTypeValue } from "../../../../../../types/enums";

export default function BuyDownOptionsWrapper(props: any) {
  const { handleSaveApplication } = props;

  const {
    buyDownOptions,
    currentSelectedInterestRate,
    setCurrentSelectedInterestRate,
    loanDetails,
    setQuickLoanSizingData,
    quickLoanSizingData,
    setDetailedLoanSizingData,
    detailedLoanSizingData,
    detailedLoanSizingDataRef,
    updateDetailedLoanSizingData,
    setNewDebouncedValue,
  } = useContext(LoansContext);

  const [formattedBuyDownOptionsState, setFormattedBuyDownOptionsState] =
    useState([]);

  const sortInterestRates = (data: Array<any>) => {
    return data?.sort((a, b) => {
      const rateA = parseFloat(a.interestRate.replace("%", ""));
      const rateB = parseFloat(b.interestRate.replace("%", ""));
      return rateA - rateB;
    });
  };

  const sortDiscountPointPercentage = (data: Array<any>) => {
    return data?.sort((a, b) => {
      const rateA = parseFloat(a.discountPointPercentage);
      const rateB = parseFloat(b.discountPointPercentage);
      return rateA - rateB;
    });
  };

  useEffect(() => {
    // Rounded-string comparison, not raw float equality — buyDownOptions'
    // interestRate is a fraction computed by the rate-sheet evaluation via
    // a different arithmetic path than currentSelectedInterestRate's
    // string-parsed percentage, so strict === essentially never matches
    // (same reasoning as LoansPagev2's updateLeverageWithBuyDownOptions,
    // which already compares this same pair via .toFixed(3) strings). A
    // failed match here meant findCurrentSelection was always undefined,
    // so the "nothing selected yet" fallback below fired on every
    // buyDownOptions refresh, resetting the loan's interestRate to
    // sortedBuydownOptions[0] regardless of what was actually selected.
    const findCurrentSelection = buyDownOptions?.find(
      (option: any) =>
        (option.interestRate * 100)?.toFixed(3) ===
        parseFloat(
          currentSelectedInterestRate?.interestRate?.replace("%", "") ?? "",
        )?.toFixed(3)
    );
    const sortedBuydownOptions = sortDiscountPointPercentage(buyDownOptions);
    let formattedBuyDownOptions: any = [];
    sortedBuydownOptions?.forEach((option: any, index : number) => {
      if (index === 0) {
        // Whatever's already selected (a manual pick, or the rate a
        // previously-approved pricing exception was raised against) takes
        // priority — sortedBuydownOptions[0] is only a fallback for when
        // nothing is selected yet. This used to feed loanEconomics.
        // interestRate from `option` (sortedBuydownOptions[0]) UNCONDITIONALLY
        // below, outside this branch — since this effect re-runs on every
        // buyDownOptions refresh (i.e. after every guideline
        // re-evaluation), that silently overwrote the user's actual
        // selection with the first sorted option's rate on every cycle,
        // right after handleInterestChange had just set the real one.
        const selectedInterestRate = findCurrentSelection
          ? currentSelectedInterestRate?.interestRate
          : `${(option?.interestRate * 100)?.toFixed(3)}%`;
        if (!findCurrentSelection) {
          setCurrentSelectedInterestRate({
            interestRate: selectedInterestRate,
            discountPoints: `${option?.discountPointPercentage?.toFixed(4)}`,
            discountPointsWithDollar: formatCurrency(
              option?.discountPointAmount
            ),
            totalBuyYield: option?.totalBuyYield,
          });
        } else {
          setCurrentSelectedInterestRate({
            interestRate: currentSelectedInterestRate?.interestRate,
            discountPoints: currentSelectedInterestRate?.discountPoints,
            discountPointsWithDollar:
              currentSelectedInterestRate?.discountPointsWithDollar,
            totalBuyYield: currentSelectedInterestRate?.totalBuyYield,
          });
        }
        const interestRateForLoanEconomics = selectedInterestRate?.replace(
          "%",
          "",
        );
        if (loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING) {
          // Functional updater, not a spread of this closure's
          // quickLoanSizingData snapshot — this effect fires on every
          // buyDownOptions change (i.e. after every fetchGuidelines cycle,
          // triggered by any field edit), and a full-object spread here
          // would silently revert any edit made to another section in the
          // meantime. Same class of bug as detailedLoanSizingData below.
          setQuickLoanSizingData((prev: any) => ({
            ...prev,
            loanEconomics: {
              ...prev?.loanEconomics,
              ["interestRate"]: interestRateForLoanEconomics,
            },
          }));
        } else {
          updateDetailedLoanSizingData((prev: any) => ({
            ...prev,
            loanEconomics: {
              ...prev?.loanEconomics,
              ["interestRate"]: interestRateForLoanEconomics,
            },
          }));
        }
      }
      formattedBuyDownOptions.push( {
        interestRate: `${(option?.interestRate * 100)?.toFixed(3)}%`,
        discountPoints: `${option?.discountPointPercentage?.toFixed(4)}`,
        discountPointsWithDollar: formatCurrency(
          option?.discountPointAmount,
          2
        ),
      });
    });
    formattedBuyDownOptions = sortInterestRates(formattedBuyDownOptions);
    setFormattedBuyDownOptionsState(formattedBuyDownOptions);
    // Narrowed to sizingType (the only loanDetails field this effect reads),
    // same fix as LoansPagev2's updateChips dependency array (877a70c42f) —
    // the full loanDetails object gets a new reference on every unrelated
    // update elsewhere on the loan (e.g. takeOutPartner assignment), which
    // was re-running this whole buyDownOptions/interestRate cascade for
    // edits that can't possibly change its result.
  }, [buyDownOptions, loanDetails?.sizingType]);

  const tableHeader = [
    {
      label: t("buyDownInterestRate"),
      headerClassName: "no-border-header border-right",
    },
    {
      label: t("discountPoints"),
      headerClassName: "no-border-header border-right",
    },
    {
      label: t("discountPointsWithDollar"),
      headerClassName: "no-border-header",
    },
  ];

  const handleInterestChange = (interestValue: any) => {
    const latestData = detailedLoanSizingData || detailedLoanSizingDataRef.current
    const currentValue = buyDownOptions?.find(
        (option: any) =>
          option.interestRate ===
          Number(+interestValue.interestRate?.replace("%", "") / 100)
      );

      const updatedInterestRate = { 
        ...interestValue, 
        totalBuyYield: currentValue.totalBuyYield 
      };

    setCurrentSelectedInterestRate(updatedInterestRate);

    if (loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING) {
      setQuickLoanSizingData({
        ...quickLoanSizingData,
        loanEconomics: {
          ...quickLoanSizingData?.loanEconomics,
          ["interestRate"]: (currentValue?.interestRate * 100).toFixed(3),
        },
      });
    } else {
      const updatedLoanData = {
        ...latestData,                    // ← has latest borrowerInformation
        loanEconomics: {
          ...latestData?.loanEconomics,
          interestRate: (currentValue?.interestRate * 100).toFixed(3),
        },
      };

      updateDetailedLoanSizingData(updatedLoanData);
    }
    handleSaveApplication(
      Boolean(loanDetails?.tcLoanId),
      "",
      "",
      updatedInterestRate
    );
    // Forces a fresh guideline/partner evaluation — LoansPagev2's
    // fetchWithDebounce effect only re-fires on newDebouncedValue (or
    // sizingType/waiverDecisionVersion) changing, none of which the
    // interestRate update above touches. Without this, the guideline API
    // is never called again with this selection, so the buy-down's rate
    // never actually reaches loanPricing.actualInterestRate as an
    // overridden value — updateDetailedLoanSizingData alone only primes
    // what the *next* (unrelated) evaluation would send.
    setNewDebouncedValue?.(updatedInterestRate?.interestRate);
  };

  const finalToorakPrice = loanDetails?.loanEconomics?.finalToorakPrice;
  const creditCommitteeDiscountPoints =
    finalToorakPrice !== undefined && finalToorakPrice !== null && finalToorakPrice !== ""
      ? (100 - Number(finalToorakPrice)).toFixed(4)
      : null;

  return (
    <div className="w-100" style={{ marginTop: "10px" }}>
      <BuyDownOptions
        header={t("buyDownOptions")}
        tableHeader={tableHeader}
        tableBody={formattedBuyDownOptionsState}
        handleInterestChange={handleInterestChange}
        currentSelectedInterestRate={currentSelectedInterestRate}
        creditCommitteeDiscountPoints={creditCommitteeDiscountPoints}
      />
    </div>
  );
}
